import { Injectable, inject } from '@angular/core';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { AuthFlowId } from '../models/auth-flow.models';
import { OnboardingAnalyticsStep } from '../models/onboarding.models';
import { AuthFlowService } from '../services/auth-flow.service';
import {
  AuthSessionStore,
  RegistrationPathFlags,
} from '../services/auth-session.store';

export function isLoginUserNotFoundMessage(message?: string): boolean {
  return Boolean(message?.includes('Пользователь не найден'));
}

export function isRegistrationEmailAlreadyExistsMessage(
  message?: string,
): boolean {
  return Boolean(message?.includes('Эта почта уже зарегистрирована'));
}

export function shouldSuppressSendCodeErrorToast(message?: string): boolean {
  return (
    isLoginUserNotFoundMessage(message) ||
    isRegistrationEmailAlreadyExistsMessage(message)
  );
}

@Injectable({ providedIn: 'root' })
export class AuthFlowAnalyticsHelper {
  private readonly analytics = inject(AnalyticsService);
  private readonly flow = inject(AuthFlowService);
  private readonly store = inject(AuthSessionStore);

  trackRegistrationTabOpened(): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — вкладка открыта',
      { tab: 'registration' },
    );
  }

  captureSendCodeButtonClick(isRegistration: boolean): void {
    if (isRegistration) {
      this.analytics.phCapture(
        'Вход: Регистрация — кнопка «Зарегистрироваться» нажата',
        { tab: 'registration' },
      );
      return;
    }

    this.analytics.phCapture('Вход: Кнопка отправки кода на почту нажата', {
      tab: 'login',
    });
  }

  captureLoginEvent(
    event: string,
    properties?: Record<string, unknown>,
  ): void {
    if (this.flow.flowId() !== AuthFlowId.Login) {
      return;
    }
    this.analytics.phCapture(event, {
      Сценарий: 'вход',
      ...properties,
    });
  }

  captureRegistrationEvent(
    event: string,
    properties?: Record<string, unknown>,
  ): void {
    if (this.flow.flowId() !== AuthFlowId.Registration) {
      return;
    }
    this.analytics.phCapture(event, {
      Сценарий: 'регистрация',
      ...properties,
    });
  }

  captureRegistrationScenarioEvent(
    event: string,
    properties?: Record<string, unknown>,
  ): void {
    this.captureRegistrationEvent(event, {
      Сценарий: 'регистрация',
      ...properties,
    });
  }

  captureRegistrationSendCodeError(message?: string): void {
    if (isRegistrationEmailAlreadyExistsMessage(message)) {
      this.captureRegistrationEvent(
        'Вход: Регистрация — почта уже в системе',
        { Сообщение: message },
      );
      return;
    }

    this.captureRegistrationEvent(
      'Вход: Регистрация — ошибка при отправке кода',
      { Сообщение: message },
    );
  }

  captureRegistrationSuccess(): void {
    const path = this.store.registrationPath();
    const sposob = this.resolveRegistrationSuccessMethod(path);

    this.captureRegistrationEvent('Вход: Успешная регистрация', {
      Способ: sposob,
      phone_skipped: path.phoneSkipped,
      onboarding_skipped: path.onboardingSkipped,
    });
  }

  rememberLoginSuccessMethod(sposob: string): void {
    this.store.loginSuccessMethod.set(sposob);
  }

  capturePendingLoginSuccess(): void {
    const sposob = this.store.loginSuccessMethod();
    if (!sposob) {
      return;
    }
    this.store.loginSuccessMethod.set(null);
    this.captureLoginEvent('Вход: Успешный вход', { Способ: sposob });
  }

  captureLoginTelInputStepShown(): void {
    this.captureLoginEvent('Вход: Шаг ввода телефона показан', {
      tel_verify_need_to_verify: true,
    });
  }

  captureLoginTelWidgetStepShown(): void {
    this.captureLoginEvent('Вход: Шаг подтверждения телефона показан');
  }

  captureLoginTelNumberUnavailable(message?: string): void {
    this.captureLoginEvent(
      'Вход: Номер телефона уже подтверждён или привязан к другой учётной записи',
      { Сообщение: message },
    );
  }

  captureLoginTelWidgetFailedToLoad(): void {
    this.captureLoginEvent('Вход: Виджет подтверждения телефона не загрузился');
  }

  captureLoginTelSkipClicked(): void {
    this.captureLoginEvent(
      'Вход: Кнопка пропуска подтверждения телефона нажата',
      { tel_verify_required: false },
    );
  }

  captureLoginTelConfirmed(): void {
    this.captureLoginEvent('Вход: Телефон подтверждён');
  }

  captureLoginSuccessAfterTelSkip(): void {
    this.rememberLoginSuccessMethod('Пропуск подтверждения телефона');
  }

  captureLoginSuccessAfterTelConfirm(): void {
    this.rememberLoginSuccessMethod('Подтверждение телефона при входе');
  }

  captureRegistrationTelInputStepShown(): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — шаг ввода телефона показан',
      { tel_verify_need_to_verify: true },
    );
  }

  captureRegistrationTelWidgetStepShown(): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — шаг подтверждения телефона показан',
    );
  }

  captureRegistrationTelNumberUnavailable(message?: string): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — номер уже подтверждён или привязан к другой учётной записи',
      { Сообщение: message },
    );
  }

  captureRegistrationTelWidgetFailedToLoad(): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — виджет подтверждения телефона не загрузился',
    );
  }

  captureRegistrationTelSkipClicked(): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — кнопка пропуска подтверждения телефона нажата',
      { tel_verify_required: false },
    );
  }

  captureRegistrationTelConfirmed(): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — телефон подтверждён',
    );
  }

  captureRegistrationOnboardingStepShown(step: OnboardingAnalyticsStep): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — шаг анкеты показан',
      { onboarding_step: step },
    );
  }

  captureRegistrationOnboardingAnswerClicked(): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — кнопка «Ответить» нажата',
    );
  }

  captureRegistrationOnboardingContinueClicked(
    step: 'question_1' | 'question_2',
  ): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — кнопка «Продолжить» нажата',
      { onboarding_step: step },
    );
  }

  captureRegistrationOnboardingSkipClicked(
    step: OnboardingAnalyticsStep,
  ): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — кнопка «Пропустить» нажата',
      { onboarding_step: step },
    );
  }

  captureRegistrationOnboardingCompleted(): void {
    this.captureRegistrationScenarioEvent(
      'Вход: Регистрация — анкета завершена',
    );
  }

  capturePvzEvent(
    event: string,
    properties?: Record<string, unknown>,
  ): void {
    if (this.flow.flowId() !== AuthFlowId.Pvz) {
      return;
    }
    this.analytics.phCapture(event, properties);
  }

  capturePvzEmployeesListShown(
    pvzUuid: string,
    employeesCount: number,
  ): void {
    this.capturePvzEvent('Вход: Экран списка сотрудников ПВЗ показан', {
      pvz_uuid: pvzUuid,
      employees_count: employeesCount,
    });
  }

  capturePvzEmployeesListEmpty(pvzUuid: string): void {
    this.capturePvzEvent('Вход: Список сотрудников ПВЗ пуст', {
      pvz_uuid: pvzUuid,
    });
  }

  capturePvzAccountSelected(): void {
    this.capturePvzEvent('Вход: Аккаунт для входа по ссылке ПВЗ выбран');
  }

  capturePvzSubstituteLoginClicked(): void {
    this.capturePvzEvent('Вход: Кнопка входа подменным по ссылке ПВЗ нажата');
  }

  capturePvzPincodeWrong(): void {
    this.capturePvzEvent('Вход: Пинкод неверен');
  }

  capturePvzSuccessByPincode(): void {
    this.capturePvzEvent('Вход: Успешный вход', {
      Способ: 'Вход по пин-коду',
    });
  }

  capturePvzSuccessBySubstitute(): void {
    this.capturePvzEvent('Вход: Успешный вход', {
      Способ: 'Вход подменным по ссылке ПВЗ',
    });
  }

  capturePvzMobileBlocked(): void {
    this.capturePvzEvent('Вход: Вход по ссылке ПВЗ недоступен на мобиле');
  }

  captureInvalidPvzLinkRedirect(): void {
    this.analytics.phCapture(
      'Вход: Невалидная ссылка ПВЗ — редирект на вход по почте',
    );
  }

  captureRegistrationOfferLinkOpened(): void {
    this.captureRegistrationScenarioEvent('Вход: Ссылка оферты открыта');
  }

  private resolveRegistrationSuccessMethod(
    path: RegistrationPathFlags,
  ): string {
    if (path.phoneSkipped && path.onboardingSkipped) {
      return 'С пропуском телефона и анкеты';
    }
    if (path.phoneSkipped) {
      return 'С пропуском телефона';
    }
    if (path.onboardingSkipped) {
      return 'С пропуском анкеты';
    }
    return 'Полная регистрация';
  }
}
